import { Inject, Injectable } from '@angular/core';
import { DataService } from '../http/dataService';
import { ENV, IEnvironment } from 'env/config';

import { Service } from '../entity';
import { CentralApis } from '../interfaces/internal';
import { Repository } from './repository';
import {
  IRouteOptimizerCreateResponse,
  IWalklist,
  RouteOptimizerResponse,
} from '../models';
import { EMPTY, Observable, of, throwError, timer } from 'rxjs';
import { ID } from '@datorama/akita';
import {
  delay,
  expand,
  last,
  map,
  mergeMap,
  retryWhen,
  switchMap,
} from 'rxjs/operators';
import { IRouteOptimizerStatusResponse } from '../models/routeOptimizer.model';

@Service({
  api: 'walklist/bulk',
  model: RouteOptimizerResponse,
})
@Injectable({ providedIn: 'root' })
export class RouteOptimizerService
  extends Repository<IRouteOptimizerCreateResponse>
{
  walklist: Repository<IWalklist>;
  territoryBaseUrl: string;

  constructor(@Inject(ENV) env: IEnvironment, dataService: DataService) {
    super({
      client: dataService,
      url: (env.api.backend.central.endpoint as CentralApis).territory,
    });

    this.territoryBaseUrl = this.url;
    this.walklist = new Repository<IWalklist>({
      client: dataService,
      url: (env.api.backend.central.endpoint as CentralApis).territory,
    });
    this.walklist.setApi('walklist/optimization');
  }

  createOptimization(
    territoryId: ID,
    parameters: any,
    useRouteSvc = false
  ): Observable<IRouteOptimizerCreateResponse> {
    this.walklist.setApi(useRouteSvc ? 'route/bulk' : 'walklist/bulk');
    const queryParams = {};
    const body = {
      territoryId,
      optimizationParameters: parameters,
    };

    return this.walklist.post(body, {}, queryParams);
  }

  getOptimizationId(territoryId: string) {
    super.setUrl(`${this.territoryBaseUrl}territory/${territoryId}`);
    super.setApi('/optimization');
    return super.getAll({}, territoryId);
  }

  // Changed exponential wait time as for DPD-1196
  // https://github.com/telus/cio-custcntrct-d2c-central/pull/667
  checkStatus(
    optimizationId: string,
    interval: number,
    retries: number,
    useRouteSvc = false
  ) {
    console.log(
      'RouteOptimization CheckStatus Interval:',
      interval,
      'ms, Retries:',
      retries
    );
    this.walklist.setApi(
      useRouteSvc ? 'route/optimization' : 'walklist/optimization'
    );

    const getStatus = () => {
      const request = this.walklist.getOne<IRouteOptimizerStatusResponse>(
        {},
        optimizationId
      );
      return request.pipe(
        map((response: IRouteOptimizerStatusResponse) => response),
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) =>
              error.status === 500
                ? throwError(() => error)
                : retries-- > 0
                ? of(error).pipe(delay(interval))
                : throwError(() => error)
            )
          )
        )
      );
    };

    return getStatus().pipe(
      expand((response) => {
        if (response.state === 4) return EMPTY;
        if (response.state === 5) {
          return throwError(() => new Error('Optimization failed'));
        }
        return timer(interval).pipe(switchMap(getStatus));
      }),
      last()
    );
  }
}
